import React, { useState, useRef, useEffect } from 'react';
import { Send, User, Bot, Mail, AlertCircle, CheckCircle, Clock, Settings, RefreshCw, Brain, Lightbulb, TrendingUp, Shield, Zap, Eye, EyeOff, MessageCircle, ArrowRight, XCircle } from 'lucide-react';

// Configuration - with fallback for browser environment
const getEnvVar = (name, defaultValue = '') => {
  try {
    return process?.env?.[name] || defaultValue;
  } catch {
    return defaultValue;
  }
};

const CONFIG = {
  DEFAULT_LAMBDA_URL: getEnvVar('REACT_APP_LAMBDA_URL', 'https://ob52fopc6rdfnnagnpdtbdqne40wcmmf.lambda-url.us-east-1.on.aws'),
  DEFAULT_CUSTOMER_EMAIL: getEnvVar('REACT_APP_DEFAULT_EMAIL', ''),
  APP_TITLE: getEnvVar('REACT_APP_TITLE', 'Phase 1 Enhanced CRM Agent'),
  ENABLE_CONFIG_UI: getEnvVar('REACT_APP_ENABLE_CONFIG', 'true') !== 'false'
};

// Conversation States (matching backend)
const CONVERSATION_STATES = {
  NEW: 'new',
  GREETING_SHOWN: 'greeting_shown',
  AWAITING_CHOICE: 'awaiting_choice',
  IN_GENERAL_FLOW: 'in_general_flow',
  IN_SPECIFIC_FLOW: 'in_specific_flow',
  COLLECTING_INPUTS: 'collecting_inputs'
};

const FLOW_TYPES = {
  GENERAL: 'general',
  SPECIFIC: 'specific'
};

const Phase1CRMApp = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: "🎉 **Welcome to the Phase 1 Enhanced CRM Assistant!**\n\nI'm your AI assistant Slavo, now featuring intelligent conversation state management:\n\n🚀 **Phase 1 Features:**\n• **Conversational Onboarding**: I'll guide you through our chat\n• **Flow Selection**: Choose between general info or specific tasks\n• **State Management**: I remember where we are in our conversation\n• **Session Recovery**: Smart timeout and error handling\n\n✨ **How it works:**\n1. I'll greet you and present clear options\n2. Choose general information or specific task help\n3. I'll guide you through the appropriate flow\n\nReady to start? Just say hello or ask me anything!",
      timestamp: new Date(),
      conversationState: CONVERSATION_STATES.NEW
    }
  ]);
  
  const [currentMessage, setCurrentMessage] = useState('');
  const [customerEmail, setCustomerEmail] = useState(CONFIG.DEFAULT_CUSTOMER_EMAIL);
  const [isLoading, setIsLoading] = useState(false);
  const [lambdaUrl, setLambdaUrl] = useState(CONFIG.DEFAULT_LAMBDA_URL);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [sessionId, setSessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [showAdvancedInfo, setShowAdvancedInfo] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  
  // Phase 1 specific state
  const [conversationState, setConversationState] = useState(CONVERSATION_STATES.NEW);
  const [flowType, setFlowType] = useState(null);
  const [requiresUserChoice, setRequiresUserChoice] = useState(false);
  const [sessionAnalytics, setSessionAnalytics] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Enhanced message formatting with better error handling
  const formatMessageContent = (content) => {
    console.log('formatMessageContent called with:', { 
      content, 
      type: typeof content,
      isNull: content === null,
      isUndefined: content === undefined 
    });
    
    // Handle null/undefined/empty content
    if (content === null || content === undefined || content === '') {
      console.warn('⚠️ Empty or null content received');
      return <div className="text-gray-500 italic">No message content received</div>;
    }
    
    // Ensure content is a string
    const safeContent = typeof content === 'string' ? content : String(content);
    
    // Process markdown-style formatting
    const lines = safeContent.split('\n');
    
    return lines.map((line, index) => {
      // Handle bold text
      let processedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      // Handle bullet points
      if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
        return (
          <div key={index} className="ml-4 mb-1">
            <span dangerouslySetInnerHTML={{ __html: processedLine }} />
          </div>
        );
      }
      
      // Handle numbered lists
      if (/^\d+\./.test(line.trim())) {
        return (
          <div key={index} className="ml-4 mb-1">
            <span dangerouslySetInnerHTML={{ __html: processedLine }} />
          </div>
        );
      }
      
      return (
        <div key={index} className={index < lines.length - 1 ? 'mb-2' : ''}>
          <span dangerouslySetInnerHTML={{ __html: processedLine }} />
        </div>
      );
    });
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Test connection on mount if URL is configured
  useEffect(() => {
    if (lambdaUrl && lambdaUrl.trim()) {
      testConnection();
    }
  }, []);

  // Fetch session analytics periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (connectionStatus === 'connected' && sessionId) {
        fetchSessionAnalytics();
      }
    }, 15000); // Every 15 seconds

    return () => clearInterval(interval);
  }, [connectionStatus, sessionId]);

  const testConnection = async () => {
    if (!lambdaUrl.trim()) {
      setConnectionStatus('error');
      setLastError('Lambda URL is required');
      return;
    }

    try {
      setConnectionStatus('testing');
      setLastError(null);
      
      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'validateSalesforceConnection'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setConnectionStatus('connected');
        console.log('✅ Connection test successful:', data);
        
        // Fetch initial analytics
        fetchSessionAnalytics();
      } else {
        const errorText = await response.text();
        setConnectionStatus('error');
        setLastError(`HTTP ${response.status}: ${errorText}`);
        console.error('❌ Connection test failed:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ Connection test failed:', error);
      setConnectionStatus('error');
      setLastError(`Network error: ${error.message}`);
    }
  };

  const fetchSessionAnalytics = async () => {
    try {
      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'getSessionAnalytics',
          sessionId: sessionId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessionAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Failed to fetch session analytics:', error);
    }
  };

  const sendMessage = async () => {
    if (!currentMessage.trim() || isLoading) return;
    
    if (!lambdaUrl.trim()) {
      addMessage('bot', 'Please configure your Lambda URL first.', true);
      return;
    }

    const userMessage = currentMessage.trim();
    setCurrentMessage('');
    setLastError(null);
    
    // Add user message
    addMessage('user', userMessage);
    setIsLoading(true);

    try {
      console.log('🚀 Sending Phase 1 request to:', lambdaUrl);
      console.log('📝 Payload:', {
        message: userMessage,
        customerEmail: customerEmail.trim() || undefined,
        sessionId: sessionId
      });

      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          customerEmail: customerEmail.trim() || undefined,
          sessionId: sessionId
        })
      });

      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP Error:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Phase 1 response data:', data);

      // ENHANCED: Always check for message content
      if (!data.message) {
        console.error('⚠️ No message in response:', data);
        data.message = 'I processed your request but no response message was generated. Please try again.';
      }

      if (data.success !== false) { // Treat as success unless explicitly false
        // Update session ID if new one was generated
        if (data.sessionId && data.sessionId !== sessionId) {
          setSessionId(data.sessionId);
        }
        
        // Update conversation state tracking
        if (data.conversationState) {
          setConversationState(data.conversationState);
        }
        if (data.flowType) {
          setFlowType(data.flowType);
        }
        setRequiresUserChoice(data.requiresUserChoice || false);
        
        // Track pending action for input collection
        if (data.conversationState === CONVERSATION_STATES.COLLECTING_INPUTS) {
          setPendingAction(data.action || data.intent);
        } else if (data.executed) {
          setPendingAction(null);
        }
        
        // Add bot response with Phase 1 conversation awareness
        addMessage('bot', data.message, false, {
          ...data.data,
          conversationState: data.conversationState,
          flowType: data.flowType,
          isGreeting: data.isGreeting,
          isFlowSelection: data.isFlowSelection,
          isGeneralInfo: data.isGeneralInfo,
          executed: data.executed,
          requiresUserChoice: data.requiresUserChoice,
          missingRequiredInputs: data.missingRequiredInputs,
          debug: data.debug
        });

        // Update analytics after successful interaction
        setTimeout(() => {
          fetchSessionAnalytics();
        }, 1000);
        
      } else {
        const errorMsg = data.error || 'An error occurred';
        const displayMessage = data.message || `❌ Error: ${errorMsg}`;
        
        addMessage('bot', displayMessage, true, {
          errorInfo: data.errorInfo,
          conversationState: data.conversationState
        });
        setLastError(errorMsg);
      }

    } catch (error) {
      console.error('❌ Error sending message:', error);
      const errorMsg = `Network error: ${error.message}`;
      addMessage('bot', `❌ Sorry, I encountered an error: ${errorMsg}\n\n🚀 **You can try:**\n• Check your connection and try again\n• Start a new conversation\n• Contact support if the issue persists`, true);
      setLastError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const addMessage = (type, content, isError = false, data = null) => {
    console.log('addMessage called with:', { 
      type, 
      contentLength: content?.length,
      isError, 
      hasData: !!data 
    });
    
    // Ensure content is never null/undefined
    const safeContent = content || 'No content provided';
    
    const newMessage = {
      id: Date.now() + Math.random(), // More unique ID
      type,
      content: safeContent,
      timestamp: new Date(),
      isError,
      data
    };
    
    setMessages(prev => [...prev, newMessage]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetSession = async () => {
    const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Try to reset on backend first
    if (connectionStatus === 'connected') {
      try {
        await fetch(lambdaUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'resetSession',
            sessionId: sessionId
          })
        });
      } catch (error) {
        console.error('Failed to reset session on backend:', error);
      }
    }
    
    // Reset frontend state
    setSessionId(newSessionId);
    setConversationState(CONVERSATION_STATES.NEW);
    setFlowType(null);
    setRequiresUserChoice(false);
    setPendingAction(null);
    
    setMessages([{
      id: Date.now(),
      type: 'bot',
      content: "🔄 **New Phase 1 Conversation Started!**\n\nHi! I'm Slavo, your AI assistant. I'm ready to help you with a fresh conversation state.\n\n**I can help you with:**\n🔍 **General Information** - Business hours, policies, services, FAQs\n⚙️ **Specific Tasks** - Case management, account operations, data updates\n\nWhat would you like help with today?",
      timestamp: new Date(),
      conversationState: CONVERSATION_STATES.NEW
    }]);
    setLastError(null);
    setSessionAnalytics(null);
  };

  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'testing':
        return <Clock className="w-4 h-4 text-yellow-500 animate-spin" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  // Phase 1: Render conversation state indicator
  const renderConversationStateIndicator = () => {
    const stateColors = {
      [CONVERSATION_STATES.NEW]: 'bg-gray-100 text-gray-700',
      [CONVERSATION_STATES.AWAITING_CHOICE]: 'bg-blue-100 text-blue-700',
      [CONVERSATION_STATES.IN_GENERAL_FLOW]: 'bg-green-100 text-green-700',
      [CONVERSATION_STATES.IN_SPECIFIC_FLOW]: 'bg-purple-100 text-purple-700',
      [CONVERSATION_STATES.COLLECTING_INPUTS]: 'bg-orange-100 text-orange-700'
    };

    const stateLabels = {
      [CONVERSATION_STATES.NEW]: 'New Session',
      [CONVERSATION_STATES.AWAITING_CHOICE]: 'Awaiting Choice',
      [CONVERSATION_STATES.IN_GENERAL_FLOW]: 'General Info',
      [CONVERSATION_STATES.IN_SPECIFIC_FLOW]: 'Specific Tasks',
      [CONVERSATION_STATES.COLLECTING_INPUTS]: 'Collecting Inputs'
    };

    return (
      <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs ${stateColors[conversationState] || 'bg-gray-100 text-gray-700'}`}>
        <MessageCircle className="w-3 h-3" />
        <span>{stateLabels[conversationState] || conversationState}</span>
        {flowType && (
          <>
            <ArrowRight className="w-3 h-3" />
            <span className="capitalize">{flowType}</span>
          </>
        )}
      </div>
    );
  };

  // Phase 1: Render choice buttons when required
  const renderChoiceButtons = () => {
    if (!requiresUserChoice || conversationState !== CONVERSATION_STATES.AWAITING_CHOICE) {
      return null;
    }

    return (
      <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb className="w-5 h-5 text-blue-600" />
          <span className="font-medium text-blue-800">Please choose how I can help you:</span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <button
            onClick={() => {
              setCurrentMessage("I want general information about your business");
              setTimeout(() => sendMessage(), 100);
            }}
            className="flex items-center gap-3 p-3 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors text-left"
          >
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Brain className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <div className="font-medium text-blue-800">General Information</div>
              <div className="text-sm text-blue-600">Hours, policies, services, FAQs</div>
            </div>
          </button>
          
          <button
            onClick={() => {
              setCurrentMessage("I need help with specific tasks");
              setTimeout(() => sendMessage(), 100);
            }}
            className="flex items-center gap-3 p-3 bg-white border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors text-left"
          >
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <Zap className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <div className="font-medium text-purple-800">Specific Tasks</div>
              <div className="text-sm text-purple-600">Case management, accounts, data</div>
            </div>
          </button>
        </div>
      </div>
    );
  };

  // Enhanced quick actions based on conversation state
  const getQuickActions = () => {
    if (conversationState === CONVERSATION_STATES.COLLECTING_INPUTS && pendingAction) {
      return [
        "subject: Test Issue, description: This is a test, customerEmail: test@example.com",
        "priority: High",
        "Cancel this action"
      ];
    } else if (conversationState === CONVERSATION_STATES.IN_GENERAL_FLOW) {
      return [
        "What are your business hours?",
        "What services do you offer?",
        "How can I contact support?",
        "Switch to specific tasks"
      ];
    } else if (conversationState === CONVERSATION_STATES.IN_SPECIFIC_FLOW) {
      return [
        "I want to create a new case",
        "Check status of a case",
        "List all my accounts",
        "Switch to general information"
      ];
    } else {
      return [
        "Hello",
        "I need help",
        "General information please",
        "Help with specific tasks"
      ];
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Enhanced Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <div className="relative">
                <Bot className="w-8 h-8 text-blue-600" />
                <MessageCircle className="w-4 h-4 text-green-500 absolute -top-1 -right-1" />
              </div>
              {CONFIG.APP_TITLE}
              <span className="text-lg font-normal text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                Phase 1
              </span>
            </h1>
            
            <div className="flex items-center gap-3">
              {/* Conversation State Indicator */}
              {renderConversationStateIndicator()}

              <div className="flex items-center gap-2 text-sm">
                {getConnectionIcon()}
                <span className={`font-medium ${
                  connectionStatus === 'connected' ? 'text-green-600' :
                  connectionStatus === 'testing' ? 'text-yellow-600' :
                  connectionStatus === 'error' ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {connectionStatus === 'connected' ? 'Connected' : 
                   connectionStatus === 'testing' ? 'Testing...' :
                   connectionStatus === 'error' ? 'Connection Error' : 'Not Connected'}
                </span>
              </div>
              
              {CONFIG.ENABLE_CONFIG_UI && (
                <button
                  onClick={() => setShowConfig(!showConfig)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Toggle Configuration"
                >
                  <Settings className="w-5 h-5" />
                </button>
              )}
              
              <button
                onClick={() => setDebugMode(!debugMode)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Toggle Debug Mode"
              >
                {debugMode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Phase 1 Features Banner */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg p-4 mb-4">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-5 h-5" />
              <span className="font-semibold">Phase 1 Conversation State Management Active</span>
            </div>
            <div className="grid md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                <span>Conversation Flow Control</span>
              </div>
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4" />
                <span>Intelligent Greeting</span>
              </div>
              <div className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4" />
                <span>Flow Selection</span>
              </div>
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                <span>Session Management</span>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {lastError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-red-700 text-sm flex-1">
                <strong>Error:</strong> {lastError}
              </div>
              <button
                onClick={() => setLastError(null)}
                className="text-red-500 hover:text-red-700"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* Configuration Section */}
          {(CONFIG.ENABLE_CONFIG_UI && showConfig) && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h3 className="font-medium text-gray-800 mb-3">Configuration</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lambda Function URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={lambdaUrl}
                      onChange={(e) => setLambdaUrl(e.target.value)}
                      placeholder="https://your-lambda-url.amazonaws.com/"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={testConnection}
                      disabled={!lambdaUrl.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 flex items-center gap-2 transition-colors"
                    >
                      {getConnectionIcon()}
                      Test
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Customer Email (Optional)
                  </label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="customer@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Phase 1 Session Info */}
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            {/* Session Info */}
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-blue-700">
                  <strong>Session:</strong> {sessionId.slice(-8)}...
                </div>
                <button
                  onClick={resetSession}
                  className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  Reset
                </button>
              </div>
              <div className="text-xs text-blue-600 space-y-1">
                <div><strong>Messages:</strong> {messages.length}</div>
                <div><strong>State:</strong> {conversationState}</div>
                {flowType && <div><strong>Flow:</strong> {flowType}</div>}
                {pendingAction && <div className="text-orange-600"><strong>Pending:</strong> {pendingAction.name || 'Action'}</div>}
              </div>
            </div>

            {/* Session Analytics */}
            {sessionAnalytics && (
              <div className="bg-green-50 rounded-lg p-3">
                <div className="text-sm text-green-700 mb-2">
                  <strong>Analytics:</strong>
                </div>
                <div className="text-xs text-green-600 space-y-1">
                  <div>Current State: {sessionAnalytics.currentState}</div>
                  <div>Session Time: {Math.round(sessionAnalytics.totalSessionTime / 60000)}m</div>
                  <div>Flow Type: {sessionAnalytics.flowType || 'None'}</div>
                  {sessionAnalytics.hasPendingExecution && (
                    <div className="text-orange-600">Has pending execution</div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-sm text-purple-700 mb-2">
                <strong>Quick Actions:</strong>
              </div>
              <div className="space-y-1">
                {getQuickActions().slice(0, 3).map((action, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentMessage(action);
                      inputRef.current?.focus();
                    }}
                    className="block w-full text-left px-2 py-1 text-xs bg-white text-purple-700 rounded hover:bg-purple-100 transition-colors truncate"
                    title={action}
                  >
                    {action}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Choice Buttons */}
          {renderChoiceButtons()}
        </div>

        {/* Enhanced Chat Container */}
        <div className="bg-white rounded-lg shadow-lg flex flex-col" style={{ height: '700px' }}>
          
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex items-start gap-3 ${
                  message.type === 'user' ? 'flex-row-reverse' : ''
                }`}
              >
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  message.type === 'user' 
                    ? 'bg-blue-500 text-white' 
                    : message.isError 
                      ? 'bg-red-100 text-red-600'
                      : 'bg-gray-100 text-gray-600'
                }`}>
                  {message.type === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                
                <div className={`flex-1 max-w-3xl ${
                  message.type === 'user' ? 'text-right' : ''
                }`}>
                  <div className={`inline-block p-3 rounded-lg ${
                    message.type === 'user'
                      ? 'bg-blue-500 text-white'
                      : message.isError
                        ? 'bg-red-50 text-red-800 border border-red-200'
                        : 'bg-gray-100 text-gray-800'
                  }`}>
                    <div className="text-sm">
                      {formatMessageContent(message.content)}
                    </div>
                  </div>
                  
                  {/* Phase 1: Show conversation state changes */}
                  {message.data?.conversationState && message.data.conversationState !== CONVERSATION_STATES.NEW && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                      <ArrowRight className="w-3 h-3" />
                      <span>State: {message.data.conversationState}</span>
                      {message.data.flowType && (
                        <>
                          <span>•</span>
                          <span>Flow: {message.data.flowType}</span>
                        </>
                      )}
                      {message.data.executed && (
                        <>
                          <span>•</span>
                          <span className="text-green-600">✓ Executed - Action completed successfully - Ready for next command</span>
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Show missing inputs indicator */}
                  {message.data?.missingRequiredInputs && message.data.missingRequiredInputs.length > 0 && (
                    <div className="mt-2 text-xs text-orange-600">
                      ⚠️ Waiting for: {message.data.missingRequiredInputs.map(i => i.name).join(', ')}
                    </div>
                  )}
                  
                  {/* Debug Information */}
                  {message.data?.debug && debugMode && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        View Debug Information
                      </summary>
                      <div className="text-xs bg-gray-50 p-3 rounded mt-1 border">
                        <pre className="whitespace-pre-wrap overflow-x-auto">
                          {JSON.stringify(message.data.debug, null, 2)}
                        </pre>
                      </div>
                    </details>
                  )}
                  
                  <div className="text-xs text-gray-500 mt-1">
                    {message.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-gray-600" />
                </div>
                <div className="bg-gray-100 text-gray-800 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <span className="text-sm ml-2">
                      {conversationState === CONVERSATION_STATES.COLLECTING_INPUTS 
                        ? 'Processing your inputs...'
                        : 'Processing with Phase 1 conversation management...'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Enhanced Input Area */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex gap-3">
              <input
                ref={inputRef}
                type="text"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  conversationState === CONVERSATION_STATES.COLLECTING_INPUTS
                    ? "Please provide the required information..."
                    : "Ask me anything - I'll guide you through our conversation..."
                }
                disabled={isLoading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              />
              <button
                onClick={sendMessage}
                disabled={!currentMessage.trim() || isLoading || connectionStatus !== 'connected'}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                <Send className="w-4 h-4" />
                Send
              </button>
            </div>
            
            <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
              <span>Press Enter to send, Shift+Enter for new line</span>
              <div className="flex items-center gap-4">
                <span>Powered by OpenAI + Salesforce + Phase 1 Conversation Management</span>
                {conversationState !== CONVERSATION_STATES.NEW && (
                  <span className="text-blue-600 font-medium">State: {conversationState}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Phase1CRMApp;